import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TextField, Button } from "@mui/material";
import { ChevronLeft, ChevronRight, Clock, MessageCircle, Send, Eye } from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import {
  getPostById,
  getComments,
  createComment,
  updateComment,
  deleteComment,
  deletePost,
} from "../../services/user_posts.js";
import { usePostViewTracker } from "../../services/usePostViewTracker.js";
import EditPostModal from "../account/edit_post.js";
import OwnerActionsMenu from "../../components/OwnerActionsMenu.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import "./post.css";

const default_profile_image_url =
  "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";

const formatDateTime = (iso) => {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const PostCarousel = ({ images = [], description }) => {
  const [index, setIndex] = useState(0);
  const total = images.length;

  if (total === 0) return null;

  const go = (delta) => setIndex((prev) => (prev + delta + total) % total);

  return (
    <div className="post-carousel">
      <div className="post-carousel-viewport">
        <img
          src={images[index]}
          alt={`Imagem ${index + 1} de ${total} — ${description || "post"}`}
          className="post-carousel-img"
        />

        {total > 1 && (
          <>
            <button
              type="button"
              className="post-carousel-btn post-carousel-btn-prev"
              onClick={() => go(-1)}
              aria-label="Imagem anterior"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              className="post-carousel-btn post-carousel-btn-next"
              onClick={() => go(1)}
              aria-label="Próxima imagem"
            >
              <ChevronRight size={24} />
            </button>
            <span className="post-carousel-counter">
              {index + 1}/{total}
            </span>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="post-carousel-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`post-carousel-dot ${i === index ? "active" : ""}`}
              onClick={() => setIndex(i)}
              aria-label={`Ir para a imagem ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PostAuthorHeader = ({ author = {}, name, actions }) => {
  const [imgError, setImgError] = useState(false);
  const photoSrc = author.profile_photo && !imgError ? author.profile_photo : default_profile_image_url;

  const avatar = (
    <img
      src={photoSrc}
      alt={`Foto de ${name}`}
      className="post-author-avatar"
      onError={() => setImgError(true)}
    />
  );

  return (
    <div className="post-author-header">
      <div className="post-author-header-main">
        {author.slug ? (
          <Link to={`/user/${author.slug}`} className="post-author-avatar-link">
            {avatar}
          </Link>
        ) : (
          avatar
        )}
        {author.slug ? (
          <Link to={`/user/${author.slug}`} className="post-author-name">
            {name}
          </Link>
        ) : (
          <span className="post-author-name">{name}</span>
        )}
      </div>
      {actions}
    </div>
  );
};

const CommentItem = ({
  comment,
  currentUserId,
  onUpdated,
  onDeleted,
}) => {
  const { getIdToken } = useAuth();
  const [imgError, setImgError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.content || "");
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [localError, setLocalError] = useState("");
  const [actionsHidden, setActionsHidden] = useState(false);

  const author = comment.author || {};
  const name = author.full_name || "Usuário";
  const photoSrc = author.profile_photo && !imgError ? author.profile_photo : default_profile_image_url;
  const isOwner =
    !actionsHidden && Boolean(currentUserId && comment.author_id === currentUserId);

  useEffect(() => {
    if (!editing) setDraft(comment.content || "");
  }, [comment.content, editing]);

  const avatar = (
    <img
      src={photoSrc}
      alt={`Foto de ${name}`}
      className="post-comment-avatar"
      onError={() => setImgError(true)}
    />
  );

  const handleSaveEdit = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    try {
      setSaving(true);
      setLocalError("");
      const token = await getIdToken();
      const updated = await updateComment(token, comment.post_id, comment.id, text);
      onUpdated?.(updated);
      setEditing(false);
    } catch (err) {
      console.error("Erro ao editar comentário:", err);
      if (err.status === 403) {
        setActionsHidden(true);
        setEditing(false);
      } else if (err.status === 404) {
        onDeleted?.(comment.id);
      } else {
        setLocalError(err.message || "Não foi possível editar o comentário.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleting) return;
    try {
      setDeleting(true);
      setLocalError("");
      const token = await getIdToken();
      await deleteComment(token, comment.post_id, comment.id);
      setPendingDelete(false);
      onDeleted?.(comment.id);
    } catch (err) {
      console.error("Erro ao excluir comentário:", err);
      if (err.status === 403) {
        setPendingDelete(false);
        setActionsHidden(true);
      } else if (err.status === 404) {
        setPendingDelete(false);
        onDeleted?.(comment.id);
      } else {
        setLocalError(err.message || "Não foi possível excluir o comentário.");
        setPendingDelete(false);
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="post-comment">
      {author.slug ? (
        <Link to={`/user/${author.slug}`} className="post-comment-avatar-link">
          {avatar}
        </Link>
      ) : (
        avatar
      )}
      <div className="post-comment-content">
        <div className="post-comment-header">
          <div className="post-comment-header-main">
            {author.slug ? (
              <Link to={`/user/${author.slug}`} className="post-comment-author">
                {name}
              </Link>
            ) : (
              <span className="post-comment-author">{name}</span>
            )}
            <span className="post-comment-date">{formatDateTime(comment.created_at)}</span>
          </div>
          {isOwner && !editing && (
            <OwnerActionsMenu
              disabled={saving || deleting}
              onEdit={() => {
                setDraft(comment.content || "");
                setEditing(true);
                setLocalError("");
              }}
              onDelete={() => setPendingDelete(true)}
            />
          )}
        </div>

        {editing ? (
          <div className="post-comment-edit">
            <TextField
              multiline
              minRows={2}
              fullWidth
              size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={saving}
            />
            <div className="post-comment-edit-actions">
              <Button
                size="small"
                onClick={() => {
                  setEditing(false);
                  setDraft(comment.content || "");
                  setLocalError("");
                }}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                size="small"
                variant="contained"
                className="post-comment-save-btn"
                onClick={handleSaveEdit}
                disabled={saving || !draft.trim() || draft.trim() === (comment.content || "").trim()}
              >
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        ) : (
          <p className="post-comment-text">{comment.content}</p>
        )}

        {localError && <p className="post-comment-error">{localError}</p>}
      </div>

      <ConfirmDialog
        open={pendingDelete}
        title="Excluir comentário"
        message="Tem certeza de que deseja excluir este comentário?"
        confirmLabel="Excluir"
        busy={deleting}
        busyLabel="Excluindo..."
        danger
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(false);
        }}
      />
    </div>
  );
};

function Post({ postId }) {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const currentUserId = user?.cognitoUser?.username || "";

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [postPendingDelete, setPostPendingDelete] = useState(false);
  const [deletingPost, setDeletingPost] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      const [postData, commentsData] = await Promise.all([
        getPostById(token, postId),
        getComments(token, postId),
      ]);
      setPost(postData);
      setComments(commentsData);
    } catch (err) {
      console.error("Erro ao carregar publicação:", err);
      setError(
        err.status === 404
          ? "Publicação não encontrada."
          : "Não foi possível carregar a publicação."
      );
    } finally {
      setLoading(false);
    }
  }, [postId, getIdToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSend = async () => {
    const text = newComment.trim();
    if (!text) return;

    try {
      setSending(true);
      const token = await getIdToken();
      const comment = await createComment(token, postId, text);
      setComments((prev) => [...prev, comment]);
      setPost((prev) =>
        prev
          ? { ...prev, comments_count: (Number(prev.comments_count) || 0) + 1 }
          : prev
      );
      setNewComment("");
    } catch (err) {
      console.error("Erro ao enviar comentário:", err);
      setError("Não foi possível enviar o comentário.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSend();
    }
  };

  const handlePostUpdated = (updated) => {
    setPost((prev) => ({ ...prev, ...updated }));
    setEditingPost(false);
  };

  const handleConfirmDeletePost = async () => {
    if (!post || deletingPost) return;
    try {
      setDeletingPost(true);
      const token = await getIdToken();
      await deletePost(token, post.id);
      navigate("/feed");
    } catch (err) {
      console.error("Erro ao excluir publicação:", err);
      if (err.status === 403) {
        setPostPendingDelete(false);
        setError("Você não tem permissão para excluir esta publicação.");
      } else if (err.status === 404) {
        navigate("/feed");
      } else {
        setError(err.message || "Não foi possível excluir a publicação.");
        setPostPendingDelete(false);
      }
    } finally {
      setDeletingPost(false);
    }
  };

  const handleCommentUpdated = (updated) => {
    setComments((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const handleCommentDeleted = (commentId) => {
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    setPost((prev) =>
      prev
        ? { ...prev, comments_count: Math.max((Number(prev.comments_count) || 1) - 1, 0) }
        : prev
    );
  };

  const viewRef = usePostViewTracker(postId, { enabled: Boolean(post) && !loading && !error });

  if (loading) return <div className="post-empty">Carregando publicação...</div>;
  if (error && !post) return <div className="post-empty">{error}</div>;
  if (!post) return <div className="post-empty">Publicação não encontrada.</div>;

  const images = post.image_urls || [];
  const author = post.author || {};
  const authorName = author.full_name || "Usuário";
  const viewsCount = Number(post.views_count) || 0;
  const isPostOwner = Boolean(currentUserId && post.author_id === currentUserId);

  return (
    <div className="post-container" ref={viewRef}>
      {error && <div className="post-inline-error">{error}</div>}

      <article className="post-card">
        <PostAuthorHeader
          author={author}
          name={authorName}
          actions={
            isPostOwner ? (
              <OwnerActionsMenu
                disabled={deletingPost}
                onEdit={() => setEditingPost(true)}
                onDelete={() => setPostPendingDelete(true)}
              />
            ) : null
          }
        />

        <PostCarousel images={images} description={post.description} />

        <h1 className="post-title">{post.title}</h1>

        <div className="post-body">
          <p className="post-description">{post.description}</p>

          <div className="post-meta">
            <span className="post-meta-item">
              <Clock size={16} /> {formatDateTime(post.created_at)}
            </span>
            <span className="post-meta-item">
              <Eye size={16} />{" "}
              {viewsCount === 1 ? "1 visualização" : `${viewsCount} visualizações`}
            </span>
          </div>
        </div>
      </article>

      <section className="post-comments-section">
        <h2 className="post-comments-title">
          <MessageCircle size={22} />
          Comentários ({comments.length})
        </h2>

        <div className="post-add-comment">
          <TextField
            label="Escreva um comentário"
            multiline
            minRows={2}
            fullWidth
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            className="post-comment-send-btn"
            onClick={handleSend}
            disabled={!newComment.trim() || sending}
            startIcon={<Send size={16} />}
          >
            {sending ? "Enviando..." : "Comentar"}
          </Button>
        </div>

        <div className="post-comments-list">
          {comments.length === 0 ? (
            <p className="post-comments-empty">
              Ainda não há comentários. Seja o primeiro a comentar!
            </p>
          ) : (
            comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                onUpdated={handleCommentUpdated}
                onDeleted={handleCommentDeleted}
              />
            ))
          )}
        </div>
      </section>

      <EditPostModal
        post={post}
        open={editingPost}
        onClose={() => setEditingPost(false)}
        onUpdated={handlePostUpdated}
      />

      <ConfirmDialog
        open={postPendingDelete}
        title="Excluir publicação"
        message="Tem certeza de que deseja excluir esta publicação? Os comentários também serão removidos."
        confirmLabel="Excluir"
        busy={deletingPost}
        busyLabel="Excluindo..."
        danger
        onConfirm={handleConfirmDeletePost}
        onCancel={() => {
          if (!deletingPost) setPostPendingDelete(false);
        }}
      />
    </div>
  );
}

export default Post;
